import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { INVOICE_STATUS, type Invoice, type InvoiceStatus, type Paginated } from '@worksy360/shared';
import { api } from '../lib/api.js';
import { Badge, Card, EmptyState, Skeleton } from '../ui/index.js';
import { label, money, shortDate, statusTone } from '../lib/format.js';

export function InvoicesList() {
  const [status, setStatus] = useState<InvoiceStatus>('sent');
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({
    queryKey: ['invoices', status],
    queryFn: () => api.get<Paginated<Invoice>>(`/invoices?status=${status}`),
  });
  const invoices = data?.items ?? [];
  const total = invoices.reduce((s, i) => s + (i.balanceCents ?? 0), 0);

  return (
    <>
      <div className="page-head">
        <div><div className="page-title">Invoices</div><div className="page-sub">{invoices.length} · {money(total)} outstanding in view</div></div>
      </div>
      <div className="row gap-2 wrap" style={{ marginBottom: 'var(--sp-4)' }}>
        {INVOICE_STATUS.map((s) => (
          <button key={s} className={`btn ${s === status ? 'btn-primary' : 'btn-secondary'} btn-sm`} onClick={() => setStatus(s)}>{label(s)}</button>
        ))}
      </div>
      <Card>
        {isLoading ? (
          <div className="card-pad stack gap-3">{[0, 1, 2].map((i) => <Skeleton key={i} h={22} />)}</div>
        ) : invoices.length === 0 ? (
          <EmptyState icon="＄" title={`No ${label(status).toLowerCase()} invoices`}>Invoices in this status will show up here.</EmptyState>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead><tr><th>Invoice</th><th>Status</th><th>Due</th><th style={{ textAlign: 'right' }}>Total</th><th style={{ textAlign: 'right' }}>Balance</th></tr></thead>
              <tbody>
                {invoices.map((i) => (
                  <tr key={i.id} onClick={() => navigate(`/customers/${i.customerId}`)}>
                    <td><strong>{i.number}</strong></td>
                    <td><Badge tone={statusTone(i.status)}>{label(i.status)}</Badge></td>
                    <td className="muted">{shortDate(i.dueDate)}</td>
                    <td className="tabular" style={{ textAlign: 'right' }}>{money(i.totalCents)}</td>
                    <td className="tabular" style={{ textAlign: 'right', color: i.balanceCents ? 'var(--color-danger)' : 'inherit' }}>{money(i.balanceCents)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </>
  );
}
